import React from 'react';
import CustomButton from '../../components/components/custom_button/customButton';
import CustomDropdown from '../../components/components/custom_list/customDropdown';
import CustomTile from '../../components/components/custom_tile/customTile';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {
    EventDonutCard,
    EventsMetricCard,
    EventToggleButton,
    formatStatNumber,
    GenderTooltip,
    MiniDemography,
    PercentLabel,
    PercentTooltip,
    SummaryGrid,
} from './stats.ui';
import {
    EVENT_ACTIVITY_COLORS,
    EVENT_DETAIL_TIME_OPTIONS,
    EVENT_FILTER_BUTTONS,
    ORGANIZER_NAME_FALLBACK,
    USERS_GENDER_FILTERS,
} from './stats.constants';
import { ReactComponent as StatusUp } from '../../components/icons/status-up.svg';
import { ReactComponent as Chart } from '../../components/icons/chart.svg';

const normalizeChartText = (value) => {
    const raw = String(value ?? '').trim();
    const compact = raw.toUpperCase().replace(/[^A-Z]/g, '');
    if (compact === 'UNKNOWN' || compact === 'UNKOWN' || compact === 'UNKONWN') return '';
    return raw;
};

export const UsersStatsSection = ({
    renderRangeFilters,
    usersRange,
    setUsersRange,
    usersYear,
    setUsersYear,
    usersMonth,
    setUsersMonth,
    usersWeekStartDate,
    setUsersWeekStartDate,
    usersWeekEndDate,
    setUsersWeekEndDate,
    usersSummaryByKey,
    usersGender,
    setUsersGender,
    usersGenderShareData,
    usersGenderTotal,
    ageData,
    cityData,
    usersRegisteredSeries,
    usersNewSeries,
    usersActiveSeries,
    usersPackSeries,
    usersConfirmedSeries,
    usersAccountDeletionsSeries,
    usersReportsSeries,
    usersNewDemography,
    usersActiveDemography,
    usersPackDemography,
}) => (
    <div className="stats-main">
        <div className="stats-section">
            <div className="stats-section-header">
                <div className="stats-section-title">Podsumowanie</div>
                {renderRangeFilters({
                    range: usersRange,
                    setRange: setUsersRange,
                    year: usersYear,
                    setYear: setUsersYear,
                    month: usersMonth,
                    setMonth: setUsersMonth,
                    weekStartDate: usersWeekStartDate,
                    setWeekStartDate: setUsersWeekStartDate,
                    weekEndDate: usersWeekEndDate,
                    setWeekEndDate: setUsersWeekEndDate,
                })}
            </div>
            <SummaryGrid
                items={[
                    usersSummaryByKey.registered,
                    usersSummaryByKey.new,
                    usersSummaryByKey.active,
                    usersSummaryByKey.inactive,
                    usersSummaryByKey.pack,
                    usersSummaryByKey.confirmed,
                ].filter(Boolean)}
            />
        </div>

        <div className="stats-section">
            <div className="stats-section-header">
                <div className="stats-section-title">Demografia</div>
                <div className="stats-toggle">
                    {USERS_GENDER_FILTERS.map((filter) => (
                        <CustomButton
                            key={filter.value}
                            name={filter.label}
                            baseClass={`custom-button-white-buttons-in-row-stats ${usersGender === filter.value ? 'active' : ''}`}
                            hoverClass="hover-effect"
                            onClick={() => setUsersGender(filter.value)}
                        />
                    ))}
                </div>
            </div>
            <div className="stats-chart-grid">
                <div className="stats-chart-card">
                    <div className="stats-chart-header">
                        <div className="stats-chart-title">Płeć</div>
                    </div>
                    <div className="stats-chart-body stats-chart-body--gender">
                        <div className="stats-gender-pie-layout">
                            <div className="stats-gender-pie-wrapper">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={usersGenderShareData}
                                            dataKey="percent"
                                            nameKey="label"
                                            isAnimationActive={false}
                                            innerRadius={52}
                                            outerRadius={82}
                                            paddingAngle={2}
                                            stroke="none"
                                            startAngle={90}
                                            endAngle={-270}
                                        >
                                            {usersGenderShareData.map((item) => (
                                                <Cell key={`gender-cell-${item.key}`} fill={item.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<GenderTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="stats-gender-pie-center">
                                    <span className="stats-gender-pie-total">
                                        {usersGenderTotal.toLocaleString('pl-PL')}
                                    </span>
                                    <span className="stats-gender-pie-caption">osób</span>
                                </div>
                            </div>
                            <div className="stats-gender-legend">
                                {usersGenderShareData.map((item) => (
                                    <div key={`gender-legend-${item.key}`} className="stats-gender-legend-item">
                                        <span
                                            className="stats-gender-legend-dot"
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <span className="stats-gender-legend-label">{item.label}</span>
                                        <span className="stats-gender-legend-value">{item.percent}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="stats-chart-card">
                    <div className="stats-chart-header">
                        <div className="stats-chart-title">Wiek (z liczbami)</div>
                    </div>
                    <div className="stats-chart-body">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={ageData} layout="vertical" margin={{ left: 10, right: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                                <YAxis type="category" dataKey="range" width={80} tickFormatter={normalizeChartText} />
                                <Tooltip content={<PercentTooltip labelText="użytkowników" />} />
                                <Bar dataKey="percent" fill="#2F5F6D" radius={[6, 6, 6, 6]} isAnimationActive={false}>
                                    <LabelList content={PercentLabel} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="stats-chart-card">
                    <div className="stats-chart-header">
                        <div className="stats-chart-title">Najczęściej sprawdzane miasta</div>
                    </div>
                    <div className="stats-chart-body">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={cityData} layout="vertical" margin={{ left: 10, right: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                                <YAxis type="category" dataKey="city" width={90} />
                                <Tooltip content={<PercentTooltip labelText="użytkowników" />} />
                                <Bar dataKey="percent" fill="#EE8F3B" radius={[6, 6, 6, 6]} isAnimationActive={false}>
                                    <LabelList content={PercentLabel} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>

        <div className="stats-section">
            <div className="stats-section-header">
                <div className="stats-section-title">Wykresy szczegółowe</div>
            </div>
            <div className="stats-paired-list">
                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{usersSummaryByKey.registered?.value}</div>
                        <div className="stats-summary-label">{usersSummaryByKey.registered?.label}</div>
                        <div className="stats-summary-note">{usersSummaryByKey.registered?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Zarejestrowani</div>
                        </div>
                        <div className="stats-chart-body">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={usersRegisteredSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                    <YAxis />
                                    <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                    <Line type="monotone" dataKey="value" stroke="#2F5F6D" strokeWidth={2} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{usersSummaryByKey.new?.value}</div>
                        <div className="stats-summary-label">{usersSummaryByKey.new?.label}</div>
                        <div className="stats-summary-note">{usersSummaryByKey.new?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Nowi</div>
                        </div>
                        <div className="stats-chart-split">
                            <div className="stats-chart-body stats-chart-body--tall">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={usersNewSeries}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                        <YAxis />
                                        <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                        <Line type="monotone" dataKey="value" stroke="#EE8F3B" strokeWidth={2} isAnimationActive={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            <MiniDemography
                                gender={usersNewDemography.gender}
                                ages={usersNewDemography.ages}
                            />
                        </div>
                    </div>
                </div>

                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{usersSummaryByKey.active?.value}</div>
                        <div className="stats-summary-label">{usersSummaryByKey.active?.label}</div>
                        <div className="stats-summary-note">{usersSummaryByKey.active?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Aktywni</div>
                        </div>
                        <div className="stats-chart-split">
                            <div className="stats-chart-body stats-chart-body--tall">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={usersActiveSeries}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                        <YAxis />
                                        <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                        <Line type="monotone" dataKey="value" stroke="#00868B" strokeWidth={2} isAnimationActive={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            <MiniDemography
                                gender={usersActiveDemography.gender}
                                ages={usersActiveDemography.ages}
                            />
                        </div>
                    </div>
                </div>

                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{usersSummaryByKey.pack?.value}</div>
                        <div className="stats-summary-label">{usersSummaryByKey.pack?.label}</div>
                        <div className="stats-summary-note">{usersSummaryByKey.pack?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Aktywność paczek</div>
                        </div>
                        <div className="stats-chart-split">
                            <div className="stats-chart-body stats-chart-body--tall">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={usersPackSeries}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                        <YAxis />
                                        <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                        <Line type="monotone" dataKey="value" stroke="#7D959F" strokeWidth={2} isAnimationActive={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            <MiniDemography
                                gender={usersPackDemography.gender}
                                ages={usersPackDemography.ages}
                            />
                        </div>
                    </div>
                </div>

                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{usersSummaryByKey.confirmed?.value}</div>
                        <div className="stats-summary-label">{usersSummaryByKey.confirmed?.label}</div>
                        <div className="stats-summary-note">{usersSummaryByKey.confirmed?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Potwierdzone wyjścia</div>
                        </div>
                        <div className="stats-chart-body">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={usersConfirmedSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                    <YAxis />
                                    <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                    <Line type="monotone" dataKey="value" stroke="#DD2626" strokeWidth={2} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{usersSummaryByKey.accountDeletion?.value}</div>
                        <div className="stats-summary-label">{usersSummaryByKey.accountDeletion?.label}</div>
                        <div className="stats-summary-note">{usersSummaryByKey.accountDeletion?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Usunięcia konta</div>
                        </div>
                        <div className="stats-chart-body">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={usersAccountDeletionsSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                    <YAxis />
                                    <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                    <Line type="monotone" dataKey="value" stroke="#4C5C68" strokeWidth={2} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{usersSummaryByKey.reports?.value}</div>
                        <div className="stats-summary-label">{usersSummaryByKey.reports?.label}</div>
                        <div className="stats-summary-note">{usersSummaryByKey.reports?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Zgłoszenia</div>
                        </div>
                        <div className="stats-chart-body">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={usersReportsSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                    <YAxis />
                                    <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                    <Line type="monotone" dataKey="value" stroke="#C84B4B" strokeWidth={2} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export const OrganizersStatsSection = ({
    renderRangeFilters,
    organizersRange,
    setOrganizersRange,
    organizersYear,
    setOrganizersYear,
    organizersMonth,
    setOrganizersMonth,
    organizersWeekStartDate,
    setOrganizersWeekStartDate,
    organizersWeekEndDate,
    setOrganizersWeekEndDate,
    organizersSummaryByKey,
    organizersKpiCards,
    organizersRegisteredSeries,
    organizersNewSeries,
    organizersKpiByKey,
    organizersPurchasedPromotionsSeries,
    organizersPromotionsValueSeries,
}) => (
    <div className="stats-main">
        <div className="stats-section">
            <div className="stats-section-header">
                <div className="stats-section-title">Podsumowanie</div>
                {renderRangeFilters({
                    range: organizersRange,
                    setRange: setOrganizersRange,
                    year: organizersYear,
                    setYear: setOrganizersYear,
                    month: organizersMonth,
                    setMonth: setOrganizersMonth,
                    weekStartDate: organizersWeekStartDate,
                    setWeekStartDate: setOrganizersWeekStartDate,
                    weekEndDate: organizersWeekEndDate,
                    setWeekEndDate: setOrganizersWeekEndDate,
                })}
            </div>
            <SummaryGrid
                items={[
                    organizersSummaryByKey.registered,
                    organizersSummaryByKey.new,
                    ...organizersKpiCards,
                ].filter(Boolean)}
                className="stats-summary-grid--organizers"
            />
        </div>

        <div className="stats-section">
            <div className="stats-paired-list">
                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{organizersSummaryByKey.registered?.value}</div>
                        <div className="stats-summary-label">{organizersSummaryByKey.registered?.label}</div>
                        <div className="stats-summary-note">{organizersSummaryByKey.registered?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Zarejestrowani organizatorzy</div>
                        </div>
                        <div className="stats-chart-body">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={organizersRegisteredSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                    <YAxis />
                                    <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                    <Line type="monotone" dataKey="value" stroke="#2F5F6D" strokeWidth={2} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{organizersSummaryByKey.new?.value}</div>
                        <div className="stats-summary-label">{organizersSummaryByKey.new?.label}</div>
                        <div className="stats-summary-note">{organizersSummaryByKey.new?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Nowi organizatorzy</div>
                        </div>
                        <div className="stats-chart-body">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={organizersNewSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                    <YAxis />
                                    <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                    <Line type="monotone" dataKey="value" stroke="#EE8F3B" strokeWidth={2} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{organizersKpiByKey.purchasedPromotions?.value}</div>
                        <div className="stats-summary-label">{organizersKpiByKey.purchasedPromotions?.label}</div>
                        <div className="stats-summary-note">{organizersKpiByKey.purchasedPromotions?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Liczba zakupionych promocji</div>
                        </div>
                        <div className="stats-chart-body">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={organizersPurchasedPromotionsSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                    <YAxis />
                                    <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                    <Line type="monotone" dataKey="value" stroke="#EE8F3B" strokeWidth={2} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{organizersKpiByKey.promotionsValue?.value}</div>
                        <div className="stats-summary-label">{organizersKpiByKey.promotionsValue?.label}</div>
                        <div className="stats-summary-note">{organizersKpiByKey.promotionsValue?.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Wartość promocji (zł)</div>
                        </div>
                        <div className="stats-chart-body">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={organizersPromotionsValueSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                    <YAxis tickFormatter={(value) => formatStatNumber(value)} />
                                    <Tooltip
                                        labelFormatter={normalizeChartText}
                                        formatter={(value, name) => [formatStatNumber(value), normalizeChartText(name)]}
                                    />
                                    <Line type="monotone" dataKey="value" stroke="#2F5F6D" strokeWidth={2} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

            </div>
        </div>

    </div>
);

export const EventsStatsSection = ({
    eventsSummaryMode,
    setEventsSummaryMode,
    renderRangeFilters,
    eventsRange,
    setEventsRange,
    eventsYear,
    setEventsYear,
    eventsMonth,
    setEventsMonth,
    eventsWeekStartDate,
    setEventsWeekStartDate,
    eventsWeekEndDate,
    setEventsWeekEndDate,
    eventsTotalEventsCard,
    eventsCategoryBreakdownItems,
    eventsAvgParticipantsCard,
    eventsAvgParticipantsSeries,
    eventsMetric,
    setEventsMetric,
    eventsCategoryFilters,
    selectedEventsCategory,
    setEventsCategory,
    eventsInterestItems,
    eventsTopInterestExpanded,
    setEventsTopInterestExpanded,
    handleOpenEventFromStats,
    toSafeMetricNumber,
    eventsTopPacksItems,
    eventsTopPacksExpanded,
    setEventsTopPacksExpanded,
}) => (
    <div className="stats-main">
        <div className="stats-section">
            <div className="stats-section-header">
                <div className="stats-section-title">Podsumowanie</div>
                <div className="stats-section-controls">
                    <div className="stats-toggle">
                        <CustomButton
                            name="Wszystkie"
                            baseClass={`custom-button-white-buttons-in-row-stats ${eventsSummaryMode === 'ALL' ? 'active' : ''}`}
                            hoverClass="hover-effect"
                            onClick={() => setEventsSummaryMode('ALL')}
                        />
                        <CustomButton
                            name="Aktywne"
                            baseClass={`custom-button-white-buttons-in-row-stats ${eventsSummaryMode === 'ACTIVE' ? 'active' : ''}`}
                            hoverClass="hover-effect"
                            onClick={() => setEventsSummaryMode('ACTIVE')}
                        />
                    </div>
                    {eventsSummaryMode === 'ALL' && renderRangeFilters({
                        range: eventsRange,
                        setRange: setEventsRange,
                        year: eventsYear,
                        setYear: setEventsYear,
                        month: eventsMonth,
                        setMonth: setEventsMonth,
                        weekStartDate: eventsWeekStartDate,
                        setWeekStartDate: setEventsWeekStartDate,
                        weekEndDate: eventsWeekEndDate,
                        setWeekEndDate: setEventsWeekEndDate,
                    })}
                </div>
            </div>
            <div className="stats-paired-list">
                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{eventsTotalEventsCard.value}</div>
                        <div className="stats-summary-label">{eventsTotalEventsCard.label}</div>
                        <div className="stats-summary-note">{eventsTotalEventsCard.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Wydarzenia a kategoria</div>
                        </div>
                        <div className="stats-chart-body stats-chart-body--tall">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={eventsCategoryBreakdownItems} layout="vertical" margin={{ left: 20, right: 70 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                                    <YAxis type="category" dataKey="category" width={170} tickFormatter={normalizeChartText} />
                                    <Tooltip content={<PercentTooltip labelText="wydarzeń" />} />
                                    <Bar dataKey="percent" fill="#7D959F" radius={[6, 6, 6, 6]} isAnimationActive={false}>
                                        <LabelList content={PercentLabel} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
                <div className="stats-paired-row">
                    <div className="stats-summary-card stats-summary-card--paired">
                        <div className="stats-summary-value">{eventsAvgParticipantsCard.value}</div>
                        <div className="stats-summary-label">{eventsAvgParticipantsCard.label}</div>
                        <div className="stats-summary-note">{eventsAvgParticipantsCard.note}</div>
                    </div>
                    <div className="stats-chart-card">
                        <div className="stats-chart-header">
                            <div className="stats-chart-title">Średnia liczba uczestników</div>
                        </div>
                        <div className="stats-chart-body">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={eventsAvgParticipantsSeries}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="label" tickFormatter={normalizeChartText} />
                                    <YAxis />
                                    <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                    <Line type="monotone" dataKey="value" stroke="#2F5F6D" strokeWidth={2} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div className="stats-section">
            <div className="stats-section-header">
                <div className="stats-section-title">Wyświetlenia / Polubienia</div>
                <div className="stats-toggle">
                    <CustomButton
                        name="Wyświetlenia"
                        baseClass={`custom-button-white-buttons-in-row-stats ${eventsMetric === 'views' ? 'active' : ''}`}
                        hoverClass="hover-effect"
                        onClick={() => setEventsMetric('views')}
                    />
                    <CustomButton
                        name="Polubienia"
                        baseClass={`custom-button-white-buttons-in-row-stats ${eventsMetric === 'likes' ? 'active' : ''}`}
                        hoverClass="hover-effect"
                        onClick={() => setEventsMetric('likes')}
                    />
                </div>
            </div>
            <div className="stats-events-category-filters" role="tablist" aria-label="Filtr kategorii wydarzeń">
                {eventsCategoryFilters.map((category) => (
                    <button
                        key={`events-category-${category.value}`}
                        type="button"
                        className={`stats-events-category-filter ${selectedEventsCategory === String(category.value) ? 'is-active' : ''}`}
                        onClick={() => setEventsCategory(String(category.value))}
                    >
                        {category.label}
                    </button>
                ))}
            </div>
            <div className="stats-events-metrics-stack">
                <EventsMetricCard
                    title="Zainteresowanie / Polubienia"
                    metricLabel={eventsMetric === 'views' ? 'Wyświetlenia' : 'Polubienia'}
                    items={eventsInterestItems}
                    barColor={eventsMetric === 'views' ? '#2F5F6D' : '#EE8F3B'}
                    expanded={eventsTopInterestExpanded}
                    onToggleExpand={() => setEventsTopInterestExpanded((previous) => !previous)}
                    onEventClick={handleOpenEventFromStats}
                    toSafeMetricNumber={toSafeMetricNumber}
                />
                <EventsMetricCard
                    title="Utworzone paczki"
                    metricLabel="Liczba paczek"
                    items={eventsTopPacksItems}
                    barColor="#2F5F6D"
                    expanded={eventsTopPacksExpanded}
                    onToggleExpand={() => setEventsTopPacksExpanded((previous) => !previous)}
                    onEventClick={handleOpenEventFromStats}
                    toSafeMetricNumber={toSafeMetricNumber}
                />
            </div>
        </div>

    </div>
);

export const EventDetailsStatsSection = ({
    eventDetailScopeValue,
    handleEventTimeScopeChange,
    selectedEventStatsData,
    selectedEventLocation,
    selectedEventDate,
    eventViewingLegend,
    eventPacksLegend,
    eventReturningLegend,
    setEventInfoModalKey,
    eventInterestBoost,
    eventViewsTotal,
    eventPacksTotal,
    eventConversionRate,
    eventAgeFilter,
    setEventAgeFilter,
    eventAgeData,
    eventAgeAxisMax,
    eventVisibleSeries,
    handleEventVisibleSeriesToggle,
    eventActivitySeries,
    eventInfoModalContent,
    eventInfoModalKey,
}) => (
    <div className="stats-main stats-main--event-detail">
        <div className="stats-event-toolbar">
            <div className="stats-section-title">Statystyki wydarzenia</div>
            <CustomDropdown
                placeholder="Zakres"
                options={EVENT_DETAIL_TIME_OPTIONS}
                value={eventDetailScopeValue}
                onChange={handleEventTimeScopeChange}
                hoverClass="hover-active"
                activeClass="is-active"
            />
        </div>

        {selectedEventStatsData ? (
            <>
                <div className="stats-event-selected-card stats-event-selected-card--figma">
                    <div className="stats-event-selected-name">{selectedEventStatsData.name}</div>
                    <div className="stats-event-selected-organizer">{selectedEventStatsData.organizerName || ORGANIZER_NAME_FALLBACK}</div>
                    <div className="stats-event-selected-meta">
                        {[selectedEventLocation, selectedEventDate].filter(Boolean).join(' • ')}
                    </div>
                </div>

                <div className="stats-event-kpi-grid">
                    <EventDonutCard
                        title="Wyświetlenia"
                        items={eventViewingLegend}
                        onInfoClick={() => setEventInfoModalKey('viewing')}
                    />
                    <EventDonutCard
                        title="Uczestnicy paczek"
                        items={eventPacksLegend}
                        onInfoClick={() => setEventInfoModalKey('packs')}
                    />
                    <EventDonutCard
                        title="Użytkownicy: nowi vs powracający"
                        items={eventReturningLegend}
                        onInfoClick={() => setEventInfoModalKey('newVsReturning')}
                    />
                </div>

                <div className="stats-event-summary-shell">
                    <div className="stats-event-summary-header">
                        <span className="stats-event-icon-badge">
                            <StatusUp />
                        </span>
                        <span>Podsumowanie</span>
                    </div>
                    <div className="stats-event-summary-grid">
                        <div className="stats-event-summary-text">
                            Twoje wydarzenie miało o {eventInterestBoost}% większe zainteresowanie
                            (mierzone liczbą osób tworzących paczki i dołączających do nich)
                            niż średnia w tej kategorii.
                        </div>
                        <div className="stats-event-summary-badge">
                            {eventInterestBoost}% większe
                        </div>
                        <div className="stats-event-summary-text">
                            Współczynnik konwersji. {formatStatNumber(eventViewsTotal)} otworzyło
                            Twoje wydarzenie, a {formatStatNumber(eventPacksTotal)} zdecydowały się
                            dołączyć (tworząc lub dołączając do paczki)
                        </div>
                        <div className="stats-event-summary-badge">
                            {eventConversionRate}%
                        </div>
                    </div>
                </div>

                <div className="stats-event-panel">
                    <div className="stats-event-panel-header">
                        <div className="stats-event-panel-title">
                            <span className="stats-event-icon-badge">
                                <Chart />
                            </span>
                            <span>Wiek</span>
                        </div>
                    </div>

                    <div className="stats-event-toggle-row">
                        {EVENT_FILTER_BUTTONS.map((filter) => (
                            <EventToggleButton
                                key={`event-age-filter-${filter.value}`}
                                label={filter.label}
                                color={filter.color}
                                active={eventAgeFilter === filter.value}
                                onClick={() => setEventAgeFilter(filter.value)}
                            />
                        ))}
                    </div>

                    <div className="stats-event-panel-chart stats-event-panel-chart--age">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={eventAgeData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                                <CartesianGrid stroke="#BEC4C8" strokeDasharray="0" vertical={false} />
                                <XAxis
                                    dataKey="range"
                                    tickFormatter={normalizeChartText}
                                    axisLine={{ stroke: '#7B7B7B', strokeWidth: 1.2 }}
                                    tickLine={false}
                                    tick={{ fill: '#666666', fontSize: 16 }}
                                />
                                <YAxis
                                    domain={[0, eventAgeAxisMax]}
                                    axisLine={{ stroke: '#7B7B7B', strokeWidth: 1.2 }}
                                    tickLine={false}
                                    tick={{ fill: '#666666', fontSize: 16 }}
                                    allowDecimals={false}
                                />
                                <Tooltip content={<PercentTooltip labelText="użytkowników" />} />
                                <Bar dataKey="count" fill="#2F5F6D" barSize={24} radius={[10, 10, 0, 0]} isAnimationActive={false} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="stats-event-panel">
                    <div className="stats-event-panel-header">
                        <div className="stats-event-panel-title">
                            <span className="stats-event-icon-badge">
                                <Chart />
                            </span>
                            <span>Aktywność użytkowników</span>
                        </div>
                        <div className="stats-event-nav">
                            <button type="button" className="stats-event-nav-button">‹‹</button>
                            <button type="button" className="stats-event-nav-button">‹</button>
                            <button type="button" className="stats-event-nav-button">›</button>
                            <button type="button" className="stats-event-nav-button is-disabled" disabled>››</button>
                        </div>
                    </div>

                    <div className="stats-event-toggle-row">
                        <EventToggleButton
                            label="Przeglądanie wydarzenia"
                            color={EVENT_ACTIVITY_COLORS.viewing}
                            active={eventVisibleSeries.viewing}
                            onClick={() => handleEventVisibleSeriesToggle('viewing')}
                        />
                        <EventToggleButton
                            label="Utworzenie paczki"
                            color={EVENT_ACTIVITY_COLORS.packs}
                            active={eventVisibleSeries.packs}
                            onClick={() => handleEventVisibleSeriesToggle('packs')}
                        />
                        <EventToggleButton
                            label="Dodanie do ulubionych"
                            color={EVENT_ACTIVITY_COLORS.favorites}
                            active={eventVisibleSeries.favorites}
                            onClick={() => handleEventVisibleSeriesToggle('favorites')}
                        />
                    </div>

                    <div className="stats-event-panel-chart stats-event-panel-chart--activity">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={eventActivitySeries} margin={{ top: 20, right: 24, left: 8, bottom: 6 }}>
                                <CartesianGrid stroke="#BEC4C8" strokeDasharray="0" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tickFormatter={normalizeChartText}
                                    axisLine={{ stroke: '#7B7B7B', strokeWidth: 1.2 }}
                                    tickLine={false}
                                    tick={{ fill: '#666666', fontSize: 16 }}
                                />
                                <YAxis
                                    axisLine={{ stroke: '#7B7B7B', strokeWidth: 1.2 }}
                                    tickLine={false}
                                    tick={{ fill: '#666666', fontSize: 16 }}
                                    allowDecimals={false}
                                />
                                <Tooltip labelFormatter={normalizeChartText} formatter={(value, name) => [value, normalizeChartText(name)]} />
                                {eventVisibleSeries.viewing && (
                                    <Bar
                                        dataKey="viewing"
                                        name="Przeglądanie wydarzenia"
                                        fill={EVENT_ACTIVITY_COLORS.viewing}
                                        radius={[4, 4, 0, 0]}
                                        isAnimationActive={false}
                                    />
                                )}
                                {eventVisibleSeries.packs && (
                                    <Bar
                                        dataKey="packs"
                                        name="Utworzenie paczki"
                                        fill={EVENT_ACTIVITY_COLORS.packs}
                                        radius={[4, 4, 0, 0]}
                                        isAnimationActive={false}
                                    />
                                )}
                                {eventVisibleSeries.favorites && (
                                    <Bar
                                        dataKey="favorites"
                                        name="Dodanie do ulubionych"
                                        fill={EVENT_ACTIVITY_COLORS.favorites}
                                        radius={[4, 4, 0, 0]}
                                        isAnimationActive={false}
                                    />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </>
        ) : (
            <div className="stats-sidebar-events-empty">
                Wybierz wydarzenie z listy po lewej stronie.
            </div>
        )}

        {eventInfoModalContent && (
            <div
                className="stats-event-info-overlay"
                onClick={() => setEventInfoModalKey(null)}
            >
                <div
                    className="stats-event-info-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label={eventInfoModalContent.title}
                    onClick={(event) => event.stopPropagation()}
                >
                    <div className="stats-event-info-header">
                        <div className="stats-event-info-title">{eventInfoModalContent.title}</div>
                        <button
                            type="button"
                            className="stats-event-info-close"
                            aria-label="Zamknij okno informacji"
                            onClick={() => setEventInfoModalKey(null)}
                        >
                            ×
                        </button>
                    </div>
                    <div className="stats-event-info-body">
                        {eventInfoModalContent.lines.map((line) => (
                            <p key={`${eventInfoModalKey}-${line}`}>{line}</p>
                        ))}
                    </div>
                </div>
            </div>
        )}
    </div>
);

export const RankingsStatsSection = ({
    rankingVisibleList,
    setRankingVisibleList,
    rankingCategories,
    rankingCategory,
    setRankingCategory,
    rankingRange,
    setRankingRange,
    rankingListConfig,
    setRankingInfoModalOpen,
    rankingInfoModalOpen,
    resolveRenderedEventImageUrl,
}) => (
    <div className="stats-main">
        <div className="stats-section">
            <div className="stats-section-header stats-ranking-section-header">
                <div className="stats-section-title">Rankingi</div>
                <div className="stats-ranking-list-switch" role="tablist" aria-label="Wybór rankingu">
                    <CustomButton
                        name="Polubienia"
                        baseClass={`custom-button-white-buttons-in-row-stats ${rankingVisibleList === 'interest' ? 'active' : ''}`}
                        hoverClass="hover-effect"
                        onClick={() => setRankingVisibleList('interest')}
                    />
                    <CustomButton
                        name="Dołączenia"
                        baseClass={`custom-button-white-buttons-in-row-stats ${rankingVisibleList === 'packs' ? 'active' : ''}`}
                        hoverClass="hover-effect"
                        onClick={() => setRankingVisibleList('packs')}
                    />
                </div>
                <div className="stats-filter-group">
                    <CustomDropdown
                        placeholder="Kategoria"
                        options={rankingCategories}
                        value={rankingCategory}
                        onChange={(event) => setRankingCategory(event.target.value)}
                        hoverClass="hover-active"
                        activeClass="is-active"
                    />
                    <CustomDropdown
                        placeholder="Zakres"
                        options={[
                            { value: 'LAST_7_DAYS', label: 'Ostatnie 7 dni' },
                            { value: 'ALL_TIME', label: 'Całość' },
                        ]}
                        value={rankingRange}
                        onChange={(event) => setRankingRange(event.target.value)}
                        hoverClass="hover-active"
                        activeClass="is-active"
                    />
                </div>
            </div>

            <div className="stats-ranking-list-container">
                <div className="stats-chart-card stats-ranking-list-card">
                    <div className="stats-ranking-list-header">
                        <div className="stats-ranking-list-title">
                            <span className="stats-ranking-list-title-accent">{rankingListConfig.titleAccent}</span>
                            <span>{rankingListConfig.titleSuffix}</span>
                        </div>
                        <button
                            type="button"
                            className="stats-ranking-info-button"
                            aria-label={`Informacja: ${rankingListConfig.infoTitle}`}
                            onClick={() => setRankingInfoModalOpen(true)}
                        >
                            i
                        </button>
                    </div>
                    <div className="stats-ranking-list-divider" />
                    {rankingListConfig.items.map((event) => (
                        <div key={`${rankingListConfig.keyPrefix}-${event.id}`} className="stats-rankingTileWrapper">
                            <CustomTile
                                imageUrl={resolveRenderedEventImageUrl(event)}
                                textLines={[event.name, event.locationLabel || `${event.place}, ${event.city}`, event.dateTimeLabel || event.date]}
                                backgroundColor={event.rank <= 3 ? '#EFEBE4' : '#F4F4F4'}
                                showNumber={true}
                                number={event.rank.toString()}
                                numberColor={event.rank <= 3 ? '#EE8F3B' : '#7D959F'}
                                numberFirst={true}
                                useAuth={false}
                                tileClassName="top-custom-tile"
                            />
                        </div>
                    ))}
                </div>
            </div>

            {rankingInfoModalOpen && (
                <div
                    className="stats-ranking-info-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label={rankingListConfig.infoTitle}
                    onClick={() => setRankingInfoModalOpen(false)}
                >
                    <div className="stats-ranking-info-modal" onClick={(event) => event.stopPropagation()}>
                        <button
                            type="button"
                            className="stats-ranking-info-close"
                            aria-label="Zamknij okno informacji"
                            onClick={() => setRankingInfoModalOpen(false)}
                        >
                            ×
                        </button>
                        <h3 className="stats-ranking-info-title">{rankingListConfig.infoTitle}</h3>
                        <p className="stats-ranking-info-description">{rankingListConfig.infoDescription}</p>
                    </div>
                </div>
            )}
        </div>
    </div>
);
